import type { AuditLog } from "@gram/client/models/components/auditlog.js";
import { agentSessionHref } from "@/pages/chatLogs/agentSessionLink";
import { mcpServerRouteParam } from "@/lib/sources";

// Builds the dashboard path to an audit subject's detail page, or null when the
// subject has no navigable page (or is missing the slug/id needed to route to
// one). Single source of truth shared by the org audit log (inline subject link
// and row-level "open" affordance) and the project Activity Timeline, so the
// destinations can never drift apart.
export function subjectHref(log: AuditLog, orgSlug: string): string | null {
  // Project-scoped subjects live under `/{org}/projects/{project}/…` and use the
  // entry's own projectSlug — which may differ from the project currently in the
  // URL, so we interpolate it rather than using the URL-bound route helpers.
  const projectBase = log.projectSlug
    ? `/${orgSlug}/projects/${log.projectSlug}`
    : null;

  switch (log.subjectType) {
    case "deployment":
      return projectBase ? `${projectBase}/deployments/${log.subjectId}` : null;
    case "toolset":
      return projectBase && log.subjectSlug
        ? `${projectBase}/toolsets/${log.subjectSlug}`
        : null;
    case "mcp_server":
      return projectBase && log.subjectSlug
        ? `${projectBase}/mcp/${mcpServerRouteParam({ id: log.subjectId, slug: log.subjectSlug })}`
        : null;
    case "environment":
      return projectBase && log.subjectSlug
        ? `${projectBase}/environments/${log.subjectSlug}`
        : null;
    case "assistant":
      return projectBase ? `${projectBase}/assistants/${log.subjectId}` : null;
    case "risk_policy":
      // PolicyCenter has no per-item route; `?policy=<id>` opens the policy.
      return projectBase
        ? `${projectBase}/risk-policies?policy=${log.subjectId}`
        : null;
    case "chat_session":
      // subjectId is the chat session UUID.
      return projectBase
        ? agentSessionHref(`${projectBase}/agent-sessions`, log.subjectId)
        : null;
    case "project":
      return log.subjectSlug ? `/${orgSlug}/projects/${log.subjectSlug}` : null;
    case "plugin":
      return projectBase ? `${projectBase}/plugins/${log.subjectId}` : null;
    // access_role and access_member are org-scoped (no project), so they route
    // under `/{org}/access/…` rather than the project tree.
    case "access_role":
      // RolesTab opens a specific role's editor via the `?editRole=<id>` param.
      return `/${orgSlug}/access/roles?editRole=${log.subjectId}`;
    case "access_member":
      return `/${orgSlug}/access/members`;
    case "killswitch_prescription":
      // The stable prescription id is sufficient. Do not fetch detail while
      // formatting the feed, which could expose private notes or create N+1s.
      return log.subjectId ? `/${orgSlug}/killswitch/${log.subjectId}` : null;
    case "api_key":
      return `/${orgSlug}/api-keys`;
    case "internal_session_issuer":
      return speakeasyProviderHref(orgSlug);
    case "json_web_key_set":
      // The Speakeasy issuer's set isn't listed on the signing-keys pages.
      if (log.metadata?.["internal_session_issuer"] === true)
        return speakeasyProviderHref(orgSlug);
      return `/${orgSlug}/signing-keys/${log.subjectId}/overview`;
    case "json_web_key": {
      if (log.metadata?.["internal_session_issuer"] === true)
        return speakeasyProviderHref(orgSlug);
      // A key event names the key as its subject; the set it belongs to rides
      // along in metadata, and the Keys tab is where the key is listed.
      const setId = log.metadata?.["json_web_key_set_id"];
      return typeof setId === "string" && setId !== ""
        ? `/${orgSlug}/signing-keys/${setId}/keys`
        : null;
    }
    default:
      return null;
  }
}

function speakeasyProviderHref(orgSlug: string): string {
  return `/${orgSlug}/identity?tab=identity-providers&provider=speakeasy`;
}
